import { useCallback } from 'react';

import log from 'loglevel';

import { useGetTaskLabels } from '../../services/taskLabels.service';
import { useInboxEntriesStore } from '../../store';
import { isAbortError } from '../../utils/isAbortError';
import { createRequestGuard } from '../requestGuard';

interface FetchTaskLabelsParams {
  taskGroupId: string;
}

const taskLabelsRequestGuard = createRequestGuard(
  () => useInboxEntriesStore.getState().taskManager.activeTaskGroup.taskGroupId,
);

let taskLabelsController: AbortController | null = null;

const startTaskLabelsController = (): AbortController => {
  taskLabelsController?.abort();
  taskLabelsController = new AbortController();
  return taskLabelsController;
};

export const useFetchTaskLabels = () => {
  const { getTaskLabels } = useGetTaskLabels();
  const { saveTaskLabels, setTaskLabelsLoading } = useInboxEntriesStore(
    (state) => state.taskManager.activeTaskGroup,
  );

  const handleFetchTaskLabels = useCallback(
    async ({ taskGroupId }: FetchTaskLabelsParams) => {
      if (taskLabelsRequestGuard.isStaleRequest(taskGroupId)) {
        return;
      }
      const requestId = taskLabelsRequestGuard.startRequest();
      const controller = startTaskLabelsController();
      setTaskLabelsLoading(true);
      try {
        const taskLabels = await getTaskLabels({
          taskGroupId,
          signal: controller.signal,
        });
        if (
          taskLabelsRequestGuard.isStaleRequest(taskGroupId) ||
          taskLabelsRequestGuard.isSupersededRequest(requestId)
        ) {
          return;
        }
        saveTaskLabels(taskLabels);
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }
        log.error('Error fetching task labels:', error);
      } finally {
        if (
          !taskLabelsRequestGuard.isStaleRequest(taskGroupId) &&
          !taskLabelsRequestGuard.isSupersededRequest(requestId)
        ) {
          setTaskLabelsLoading(false);
        }
      }
    },
    [getTaskLabels, saveTaskLabels, setTaskLabelsLoading],
  );

  return {
    handleFetchTaskLabels,
  };
};
